import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { rejectParticipantAudio, rejectParticipantVideo } from '../../../av-moderation/actions';
import participantsPaneTheme from '../../../base/components/themes/participantsPaneTheme.json';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getParticipantById, isScreenShareParticipant } from '../../../base/participants/functions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Input from '../../../base/ui/components/web/Input';
import useContextMenu from '../../../base/ui/hooks/useContextMenu.web';
import { normalizeAccents } from '../../../base/util/strings.web';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { isButtonEnabled, showOverflowDrawer } from '../../../toolbox/functions.web';
import { muteRemote } from '../../../video-menu/actions.web';
import { getSortedParticipantIds, isCurrentRoomRenamable, shouldRenderInviteButton } from '../../functions';
import { useParticipantDrawer } from '../../hooks';
import RenameButton from '../breakout-rooms/components/web/RenameButton';
import { InviteButton } from './InviteButton';
import MeetingParticipantContextMenu from './MeetingParticipantContextMenu';
import MeetingParticipantItems from './MeetingParticipantItems';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { openDialog } from '../../../base/dialog/actions';
import MuteEveryoneDialog from '../../../video-menu/components/web/MuteEveryoneDialog';
import { IconSearch, IconCloseLarge } from '../../../base/icons/svg';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import MuteAllButton from './MuteAllButton';
import { close } from '../../actions.web';
const useStyles = makeStyles()(theme => {
    return {
        headingW: {
            color: theme.palette.warning02
        },
        heading: {
            color: theme.palette.text01,
            ...withPixelLineHeight(theme.typography.heading6),
            marginBottom: theme.spacing(3),
            [`@media(max-width: ${participantsPaneTheme.MD_BREAKPOINT})`]: {
                ...withPixelLineHeight(theme.typography.bodyShortBoldLarge)
            },
            width: '72%'
        },
        search: {
            // margin: `${theme.spacing(3)} 0`,
            '& input': {
                textAlign: 'left',
                paddingRight: '0px',
                background: '#808080',
                paddingLeft: '4px',
                '&:hover': {
                    background: "#6b6868"
                },
                '&:-webkit-autofill:hover': {
                    "-webkitBoxShadow": "0 0 0 30px #6b6868 inset !important",
                    "-webkitTextFillColor": "#ffffff !important"
                }
            }
        },
        headingContainer: {
            display: 'flex',
            alignItems: 'self-start',
            padding: '0 24px',
            marginBottom: '24px',
            marginTop: '8px'
        },
        headingButtonContainer: {
            display: 'flex',
            // marginLeft: 'auto',
            marginRight: '8px'
        },
        clickButtons: {
            margin: '0 6px',
            '&:hover': {
                backgroundColor: `${theme.palette.ui12} !important`
            },
        },
        searchContainer: {
            display: 'flex',
            boxShadow: `inset 0px -1px 0px ${theme.palette.ui12}`,
            padding: '8px 24px',
            '& > button': {
                marginLeft: 'auto'
            }
        },
        toggledButton: {
            backgroundColor: 'none !important'
        },
        participantsContainer: {
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: '40%',
            flexGrow: 1,
            '> .participant-listItem ': {
                margin: 0
            }
        }
    };
});
/**
 * Renders the MeetingParticipantList component.
 * NOTE: This component is not using useSelector on purpose. The child components MeetingParticipantItem
 * and MeetingParticipantContextMenu are using connect. Having those mixed leads to problems.
 * When this one was using useSelector and the other two were not -the other two were re-rendered before this one was
 * re-rendered, so when participant is leaving, we first re-render the item and menu components,
 * throwing errors (closing the page) before removing those components for the participant that left.
 *
 * @returns {ReactNode} - The component.
 */
function MeetingParticipants({ currentRoom, overflowDrawer, participantsCount, searchString, setSearchString, showInviteButton, sortedParticipantIds = [] }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [lowerMenu, , toggleMenu, menuEnter, menuLeave, raiseContext] = useContextMenu();
    const muteAudio = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.AUDIO));
        dispatch(rejectParticipantAudio(id));
    }, [dispatch]);
    const stopVideo = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.VIDEO));
        dispatch(rejectParticipantVideo(id));
    }, [dispatch]);
    const [drawerParticipant, closeDrawer, openDrawerForParticipant] = useParticipantDrawer();
    const showFooter = useSelector(isLocalParticipantModerator);
    const [inputString, setInputValue] = useState(searchString);
    // FIXME:
    // It seems that useTranslation is not very scalable. Unmount 500 components that have the useTranslation hook is
    // taking more than 10s. To workaround the issue we need to pass the texts as props. This is temporary and dirty
    // solution!!!
    // One potential proper fix would be to use react-window component in order to lower the number of components
    // mounted.
    const participantActionEllipsisLabel = t('participantsPane.actions.moreParticipantOptions');
    const youText = t('chat.you');
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const _isCurrentRoomRenamable = useSelector(isCurrentRoomRenamable);
    const { classes: styles } = useStyles();
    const togStyles = { backgroundColor: 'none !important' };
    const onMuteAll = useCallback(() => {
        dispatch(openDialog(MuteEveryoneDialog));
    }, []);
    const _onInputChange = ((change) => {
        const value = typeof change === 'object' ? change.target.value : change;
        setInputValue(value);
        if (value == '') {
            setSearchString(inputString);
        }
    });
    const onClick = (() => {
        setSearchString(inputString);
    });
    const onClosePane = useCallback(() => {
        dispatch(close());
    }, []);
    const onMenuClick = () => {
        //This would hide menu when clicked on a menu item 
        lowerMenu(true);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { "aria-level": 1, className: 'sr-only', role: 'heading' }, t('participantsPane.title')),
        React.createElement("div", { className: styles.headingContainer },
            React.createElement("div", { className: styles.heading },
                currentRoom?.name
                    ? `${currentRoom.name} (${participantsCount})`
                    : t('participantsPane.headings.participantsList', { count: participantsCount }),
                currentRoom?.name && _isCurrentRoomRenamable
                    && React.createElement(RenameButton, { breakoutRoomJid: currentRoom?.jid, name: currentRoom?.name })),
            showFooter && (React.createElement("div", { className: styles.headingButtonContainer },
                React.createElement(MuteAllButton, null))),
            React.createElement(ClickableIcon, { accessibilityLabel: t('participantsPane.close', 'Close'), icon: IconCloseLarge, onClick: onClosePane })),
        showInviteButton && React.createElement(InviteButton, null),
        React.createElement("div", { className: styles.searchContainer },
            React.createElement(Input, { accessibilityLabel: t('participantsPane.search'), className: styles.search, clearable: true, id: 'participants-search-input', onChange: _onInputChange, placeholder: t('participantsPane.search'), value: inputString }),
            React.createElement(ClickableIcon, { accessibilityLabel: t('participantsPane.actions.muteAll'), icon: IconSearch, onClick: onClick, className: styles.clickButtons })),
        React.createElement("div", { className: styles.participantsContainer },
            React.createElement(MeetingParticipantItems, { isInBreakoutRoom: isBreakoutRoom, lowerMenu: lowerMenu, muteAudio: muteAudio, openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: overflowDrawer, participantActionEllipsisLabel: participantActionEllipsisLabel, participantIds: sortedParticipantIds, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), stopVideo: stopVideo, toggleMenu: toggleMenu, youText: youText })),
        React.createElement(MeetingParticipantContextMenu, { closeDrawer: closeDrawer, drawerParticipant: drawerParticipant, muteAudio: muteAudio, offsetTarget: raiseContext?.offsetTarget, onEnter: menuEnter, onLeave: menuLeave, onSelect: onMenuClick, overflowDrawer: overflowDrawer, participantID: raiseContext?.entity })));
}
/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    let sortedParticipantIds = getSortedParticipantIds(state);
    // Filter out the virtual screenshare participants since we do not want them to be displayed as separate
    // participants in the participants pane.
    sortedParticipantIds = sortedParticipantIds.filter((id) => {
        const participant = getParticipantById(state, id);
        return !isScreenShareParticipant(participant);
    });
    const participantsCount = sortedParticipantIds.length;
    const showInviteButton = shouldRenderInviteButton(state) && isButtonEnabled('invite', state);
    const overflowDrawer = showOverflowDrawer(state);
    const currentRoomId = getCurrentRoomId(state);
    const currentRoom = getBreakoutRooms(state)[currentRoomId];
    return {
        currentRoom,
        overflowDrawer,
        participantsCount,
        showInviteButton,
        sortedParticipantIds
    };
}
export default connect(_mapStateToProps)(MeetingParticipants);
